import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import { useAccesos } from '../contexts/AccesosContext';

function GestionModulosForm({ open, onClose, onSuccess, modulo, user }) {
  const server = process.env.REACT_APP_API_ROUTE;
  const { tienePermiso } = useAccesos();
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    idModulo: '',
    nombreModulo: '',
    idTipo: '',
    etiquetaInforme: '',
    orden: ''
  });
  
  // Estados para opciones de select
  const [tiposModulo, setTiposModulo] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Efecto para cargar tipos de módulo
  useEffect(() => {
    const fetchTiposModulo = async () => {
      try {
        const response = await fetch(`${server}/MgModuloes`);
        if (response.ok) {
          const data = await response.json();
          const apiTipos = data.$values || data || [];
          
          // Agregar las dos opciones específicas
          const tiposConNuevasOpciones = [
            { idTipo: 1, descripcionTipo: 'LABORATORIO' },
            { idTipo: 2, descripcionTipo: 'ADMINISTRACIÓN' },
            ...apiTipos.filter(tipo => tipo.idTipo !== 1 && tipo.idTipo !== 2)
          ];
          
          setTiposModulo(tiposConNuevasOpciones);
        }
      } catch (error) {
        console.error('Error al cargar tipos de módulo:', error);
        // En caso de error, usar solo las opciones hardcodeadas
        setTiposModulo([
          { idTipo: 1, descripcionTipo: 'LABORATORIO' },
          { idTipo: 2, descripcionTipo: 'ADMINISTRACIÓN' }
        ]);
      }
    };

    if (open) {
      fetchTiposModulo();
    }
  }, [open, server]);

  // Efecto para cargar datos del módulo cuando se está editando
  useEffect(() => {
    if (modulo) {
      setFormData({
        idModulo: modulo.idModulo || '',
        nombreModulo: modulo.nombreModulo || '',
        idTipo: modulo.idTipo || '',
        etiquetaInforme: modulo.etiquetaInforme || '',
        orden: modulo.orden || ''
      });
    } else {
      // Reset form for new module
      setFormData({
        idModulo: '',
        nombreModulo: '',
        idTipo: '',
        etiquetaInforme: '',
        orden: ''
      });
    }
    setErrors({});
  }, [modulo, open]);

  // Validación del formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.idModulo.trim()) {
      newErrors.idModulo = 'ID Módulo es requerido';
    }

    if (!formData.nombreModulo.trim()) {
      newErrors.nombreModulo = 'Nombre del módulo es requerido';
    }

    if (!formData.idTipo) {
      newErrors.idTipo = 'Tipo de módulo es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejador de cambios en el formulario
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Manejador de envío del formulario
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        idModulo: formData.idModulo.trim(),
        nombreModulo: formData.nombreModulo.trim(),
        idTipo: parseInt(formData.idTipo),
        etiquetaInforme: formData.etiquetaInforme.trim() || null,
        orden: formData.orden ? parseInt(formData.orden) : null,
        ...(modulo ? {
          // Para edición - datos de auditoría
          modificadoPor: user?.codigoUsuario || 'ADMIN',
          fechaModificacion: new Date().toISOString(),
          modificacion: 2
        } : {
          // Para creación - datos de auditoría
          ingresadoPor: user?.codigoUsuario || 'ADMIN',
          fechaIngreso: new Date().toISOString(),
          modificacion: 1
        })
      };

      const url = modulo 
        ? `${server}/MgModuloes/${modulo.idModulo}`
        : `${server}/MgModuloes`;

      const method = modulo ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const successMessage = modulo 
          ? 'Módulo actualizado correctamente'
          : 'Módulo creado correctamente';
        
        alert(successMessage);
        onSuccess();
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 
          (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 
          'Error al guardar el módulo');
        console.error('Error del servidor:', errorData);
        alert('Error al guardar el módulo: ' + errorMessage);
      }
    } catch (error) {
      console.error('Error al guardar módulo:', error);
      alert('Error al guardar el módulo');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {modulo ? 'Editar Módulo' : 'Nuevo Módulo'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID Módulo *"
                value={formData.idModulo}
                onChange={handleChange('idModulo')}
                error={!!errors.idModulo}
                helperText={errors.idModulo}
                disabled={!!modulo} // No permitir cambiar ID al editar
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.idTipo}>
                <InputLabel>Tipo de Módulo *</InputLabel>
                <Select
                  value={formData.idTipo}
                  onChange={handleChange('idTipo')}
                  label="Tipo de Módulo *"
                >
                  {tiposModulo.map((tipo) => (
                    <MenuItem key={tipo.idTipo} value={tipo.idTipo}>
                      {tipo.descripcionTipo}
                    </MenuItem>
                  ))}
                </Select>
                {errors.idTipo && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {errors.idTipo}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Módulo *"
                value={formData.nombreModulo}
                onChange={handleChange('nombreModulo')}
                error={!!errors.nombreModulo}
                helperText={errors.nombreModulo}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Etiqueta Informe"
                value={formData.etiquetaInforme}
                onChange={handleChange('etiquetaInforme')}
                helperText="Texto que aparecerá en los informes"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orden"
                type="number"
                value={formData.orden}
                onChange={handleChange('orden')}
                helperText="Orden de visualización"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !tienePermiso('Crear Módulo')}
          color="primary"
        >
          {loading ? 'Guardando...' : (modulo ? 'Actualizar' : 'Crear')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GestionModulosForm;
